import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TagInput({ value = [], onChange, token, placeholder = 'Add a tag...' }) {
  const [input, setInput] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch all tags once
  useEffect(() => {
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/tags`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setAllTags(res.data.tags || [])).catch(() => {});
  }, [token]);

  const filtered = input.trim()
    ? allTags.filter(t => t.toLowerCase().includes(input.toLowerCase()) && !value.includes(t)).slice(0, 8)
    : [];

  const addTag = useCallback((tag) => {
    const trimmed = tag.trim();
    if (!trimmed || value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInput('');
    setShowDropdown(false);
    inputRef.current?.focus();
  }, [value, onChange]);

  const removeTag = (tag) => {
    onChange(value.filter(t => t !== tag));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) {
        addTag(filtered[0]);
      } else if (input.trim()) {
        addTag(input);
      }
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div ref={containerRef} className="relative" data-testid="tag-input-container">
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[36px] px-2.5 py-1.5 border rounded-md bg-background text-sm focus-within:ring-2 focus-within:ring-ring/20 focus-within:border-ring cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="text-xs gap-1 pr-1 shrink-0"
            data-testid={`tag-chip-${tag}`}
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              data-testid={`tag-remove-${tag}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowDropdown(true); }}
          onFocus={() => { if (input.trim()) setShowDropdown(true); }}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[80px] bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          data-testid="tag-input-field"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg bg-card shadow-lg overflow-hidden" data-testid="tag-dropdown">
          {filtered.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => addTag(tag)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              data-testid={`tag-option-${tag}`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
